/* global toggleEventModuleUrl fetchEventSetupUrl locales currencies updateEventUrl */
/* global contingencyCanBeDisabled costTemplateCanBeDisabled costPackCanBeDisabled */
/* global enabledModuleSportDepartment isModuleSportDepartmentChangeable isEventCashflowDateSet enabledModuleVik enabledModuleApprovals isSuperAdmin */
/* global procurementEmailMembers */

import React, { forwardRef, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import { usePopperTooltip } from 'react-popper-tooltip';
import CreatableSelect from 'react-select/creatable';

import ModuleTogglesPanel from './ModuleTogglesPanel';
import SectionVisibilityPanel from './SectionVisibilityPanel';
import StatusRulesCard from './StatusRulesCard';
import TimeLockCard from './TimeLockCard';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import ContentList from 'CommonComponents/ContentList';
import ContentListItem from 'CommonComponents/ContentListItem';
import DatePickerField from 'CommonComponents/DatePickerField';
import FormInput from 'CommonComponents/FormInput';
import SectionTile from 'CommonComponents/SectionTile';
import SelectInput from 'CommonComponents/SelectInput';

import { ConfirmationModalProvider } from 'Context/ConfirmationModalContext';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import { useToast } from 'Hooks/useToast';

import CircleCheckIcon from 'Assets/icons/circle-check.svg';
import CircleXMarkIcon from 'Assets/icons/circle-xmark.svg';
import HourglassIcon from 'Assets/icons/hourglass.svg';
import InformationCircleIcon from 'Assets/icons/information-circle.svg';
import LinkIcon from 'Assets/icons/link.svg';

const TYPE_EVENT_SETUP = 'EVENT_SETUP';
const TYPE_MODULES = 'MODULES';
const TYPE_EVENT_FUNCTIONALITY = 'EVENT_FUNCTIONALITY';
const TYPE_COMMITMENT_SETTINGS = 'COMMITMENT_SETTINGS';
const TYPE_VENUES = 'VENUES';
const TYPE_SPORTS = 'SPORTS';
const TYPE_DEP_CC = 'DEP_CC';
const TYPE_WORKFORCE = 'WORKFORCE';
const TYPE_PROCUREMENTS = 'PROCUREMENTS';
const TYPE_RISKS = 'RISKS';
const TYPE_SUPER_USER = 'SUPER_USER';

const MODULE_TICKETING = 'ticketing';
const MODULE_SPONSORSHIP = 'sponsorship';
const MODULE_VIK = 'vik_enabled';
const MODULE_CONTINGENCY = 'budget_change_and_contingency_enabled';
const MODULE_SPORT = 'sport_enabled';
const MODULE_PR = 'pr_enabled';
const MODULE_EA = 'ea_enabled';
const MODULE_EXPENSE_DIRECT_COMMIT = 'expense_direct_commit_enabled';
const SETTING_PR_REQUIRED_ABOVE = 'pr_required_above';
const SETTING_PR_EMAIL_ENABLED = 'pr_approval_email_enabled';
const SETTING_PR_EMAIL_RECIPIENTS = 'pr_approval_email_recipients';
const SETTING_EA_EMAIL_ENABLED = 'ea_approval_email_enabled';
const SETTING_EA_EMAIL_RECIPIENTS = 'ea_approval_email_recipients';
const SETTING_DC_EMAIL_ENABLED = 'dc_approval_email_enabled';
const SETTING_DC_EMAIL_RECIPIENTS = 'dc_approval_email_recipients';
const MODULE_REVENUE_DIRECT_COMMIT = 'revenue_direct_commit_enabled';
const MODULE_COMMIT_EXCEED_BUDGET = 'commit_exceed_budget_enabled';
const MODULE_EVENT_PROFIT = 'event_profit_enabled';
const MODULE_COST_TEMPLATE = 'cost_template_enabled';
const MODULE_COST_PACK = 'cost_pack_enabled';
const MODULE_DEPARTMENTAL_PROJECT_PLANS = 'departmental_project_plans_enabled';
const MODULE_VENUE_PROJECT_PLANS = 'venue_project_plans_enabled';
const MODULE_MARK_UP = 'mark_up_enabled';
const MODULE_VENUE_RAID = 'venue_raid_enabled';
const MODULE_SUPPLIER_PORTAL = 'supplier_portal_enabled';
const MODULE_DEPT_PROCUREMENT_PLAN = 'dept_procurement_plan_enabled';
const MODULE_EMBEDDED_SOURCE_LINKING = 'embedded_source_linking_enabled';
// A section-visibility key rather than an events column: `true` means shown.
const SECTION_PROCUREMENT_EVALUATION = 'procurement_evaluation';

// Workspace-member options for the recipient pickers, sourced from the Blade-injected
// global (array of { name, email }). Read defensively in case the view omits it.
const procurementMembers = typeof procurementEmailMembers !== 'undefined' ? procurementEmailMembers : [];
const procurementMemberOptions = procurementMembers
    .filter((member) => member && member.email)
    .map((member) => ({ label: member.name ? `${member.name} <${member.email}>` : member.email, value: member.email }));

function EmailRecipientsInput({ value, onChange, placeholder }) {
    const selected = (value || []).map((email) => ({ label: email, value: email }));

    return (
        <CreatableSelect
            isMulti
            className="react-select-container"
            classNamePrefix="react-select"
            options={procurementMemberOptions}
            value={selected}
            placeholder={placeholder}
            onChange={(values) => onChange((values || []).map((option) => option.value))}
            formatCreateLabel={(input) => `${$t('Add')} "${input}"`}
        />
    );
}

export default function EventSetup(props) {
    const [datasets, setDatasets] = useState({});
    const [modules, setModules] = useState({});
    const [prSettings, setPrSettings] = useState({
        [SETTING_PR_REQUIRED_ABOVE]: '',
        [SETTING_PR_EMAIL_ENABLED]: false,
        [SETTING_PR_EMAIL_RECIPIENTS]: [],
        [SETTING_EA_EMAIL_ENABLED]: false,
        [SETTING_EA_EMAIL_RECIPIENTS]: [],
        [SETTING_DC_EMAIL_ENABLED]: false,
        [SETTING_DC_EMAIL_RECIPIENTS]: [],
    });
    const [collapsed, setCollapsed] = useState({
        [TYPE_EVENT_SETUP]: true,
        [TYPE_MODULES]: true,
        [TYPE_EVENT_FUNCTIONALITY]: true,
        [TYPE_COMMITMENT_SETTINGS]: true,
        [TYPE_VENUES]: true,
        [TYPE_SPORTS]: true,
        [TYPE_DEP_CC]: true,
        [TYPE_WORKFORCE]: true,
        [TYPE_PROCUREMENTS]: true,
        [TYPE_RISKS]: true,
        [TYPE_SUPER_USER]: true,
    });
    const [event, setEvent] = useState(null);
    // The event's route-bound slug is regenerated from `name` on every save
    // (Event::getSlugOptions() has no doNotGenerateSlugsOnUpdate()), which
    // moves every URL below out from under the page they were baked into at
    // render time. Tracked as state so a rename can rewrite them in place.
    const [fetchUrl, setFetchUrl] = useState(props.fetchEventSetupUrl);
    const [updateUrl, setUpdateUrl] = useState(props.updateEventUrl);
    const [canToggleModules, setCanToggleModules] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [showWarning, setShowWarning] = useState(false);
    const [fetched, setFetched] = useState(false);
    const [request, setRequest] = useState(false);

    const [vikCanBeEnabled, setVikCanBeEnabled] = useState(false);

    // Supplier-request recipient emails (IB-610). Kept as a comma/space separated string
    // in the input, parsed to an array of trimmed addresses when saving.
    const [supplierRequestRecipients, setSupplierRequestRecipients] = useState('');

    const { getArrowProps, getTooltipProps, setTooltipRef, setTriggerRef, visible } = usePopperTooltip({ placement: 'bottom', trigger: 'click' });

    const toast = useToast();

    const confirmDisableVikModule = useConfirmationModal({
        title: $t('Turn off VIK'),
        message: $t(
            'It is not possible to turn off VIK unless all committed and targeted VIK has been removed from both revenue and expenditure. Please remove all items allocated as VIK and try again.',
        ),
        actions: [
            {
                label: $t('OK'),
                variant: 'primary',
            },
        ],
    });

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const expand = urlParams.get('expand_set_up');

        if (expand) {
            toggleCollapsed(TYPE_EVENT_SETUP);
        }
        fetchDataset();
    }, []);

    useEffect(() => {
        if (fetched && datasets.event) {
            if (datasets.event.start_date > startDate || datasets.event.end_date < endDate) {
                setShowWarning(true);
            } else {
                setShowWarning(false);
            }
        }
    }, [datasets]);

    useEffect(() => {
        setVikCanBeEnabled(!modules[MODULE_MARK_UP]);
    }, [modules]);

    const fetchDataset = (url) => {
        axios
            .get(url || fetchUrl)
            .then(({ data }) => {
                setDatasets(data);
                const event = data.event;
                setStartDate(event?.start_date);
                setEndDate(event?.end_date);
                const evaluation = data.canManageSectionVisibility
                    ? (data.sections || []).find((section) => section.key === SECTION_PROCUREMENT_EVALUATION)
                    : undefined;
                setModules({
                    [MODULE_TICKETING]: !!data.event?.ticketing,
                    [MODULE_CONTINGENCY]: !!data.event?.budget_change_and_contingency_enabled,
                    [MODULE_SPONSORSHIP]: !!data.event?.sponsorship,
                    [MODULE_VIK]: !!data.event?.vik_enabled,
                    [MODULE_PR]: !!data.event?.pr_enabled,
                    [MODULE_EA]: !!data.event?.ea_enabled,
                    [MODULE_EXPENSE_DIRECT_COMMIT]: !!data.event?.expense_direct_commit_enabled,
                    [MODULE_REVENUE_DIRECT_COMMIT]: !!data.event?.revenue_direct_commit_enabled,
                    [MODULE_COMMIT_EXCEED_BUDGET]: !!data.event?.commit_exceed_budget_enabled,
                    [MODULE_EVENT_PROFIT]: !!data.event?.event_profit_enabled,
                    [MODULE_SPORT]: !!data.is_sport_event || false,
                    [MODULE_COST_TEMPLATE]: !!data.event?.cost_template_enabled,
                    [MODULE_COST_PACK]: !!data.event?.cost_pack_enabled,
                    [MODULE_DEPARTMENTAL_PROJECT_PLANS]: !!data.event?.departmental_project_plans_enabled,
                    [MODULE_VENUE_PROJECT_PLANS]: !!data.event?.venue_project_plans_enabled,
                    [MODULE_MARK_UP]: !!data.event?.mark_up_enabled,
                    [MODULE_VENUE_RAID]: !!data.event?.venue_raid_enabled,
                    [MODULE_SUPPLIER_PORTAL]: !!data.event?.supplier_portal_enabled,
                    [MODULE_DEPT_PROCUREMENT_PLAN]: !!data.event?.dept_procurement_plan_enabled,
                    [MODULE_EMBEDDED_SOURCE_LINKING]: !!data.event?.embedded_source_linking_enabled,
                    ...(evaluation ? { [SECTION_PROCUREMENT_EVALUATION]: !!evaluation.visible } : {}),
                });
                setPrSettings({
                    [SETTING_PR_REQUIRED_ABOVE]: data.event?.pr_required_above ?? '',
                    [SETTING_PR_EMAIL_ENABLED]: !!data.event?.pr_approval_email_enabled,
                    [SETTING_PR_EMAIL_RECIPIENTS]: data.event?.pr_approval_email_recipients ?? [],
                    [SETTING_EA_EMAIL_ENABLED]: !!data.event?.ea_approval_email_enabled,
                    [SETTING_EA_EMAIL_RECIPIENTS]: data.event?.ea_approval_email_recipients ?? [],
                    [SETTING_DC_EMAIL_ENABLED]: !!data.event?.dc_approval_email_enabled,
                    [SETTING_DC_EMAIL_RECIPIENTS]: data.event?.dc_approval_email_recipients ?? [],
                });
                setSupplierRequestRecipients((data.event?.supplier_request_recipients || []).join(', '));
                setCanToggleModules(data.canToggleModules);
                setEvent(data.event);
                setVikCanBeEnabled(!!data.vik_can_be_enabled);
            })
            .catch((e) => {
                toast.error($t('Error fetching event set up'));
            })
            .finally(() => {
                setFetched(true);
            });
    };

    const parseRecipients = (value) =>
        value
            .split(/[\s,;]+/)
            .map((email) => email.trim())
            .filter((email) => email.length > 0);

    const toggleModule = () => {
        setRequest(true);
        const threshold = prSettings[SETTING_PR_REQUIRED_ABOVE];
        // Mark-up is read only for the VIK lock; it is switched on the set-up wizard's Functionality step.
        const { [MODULE_MARK_UP]: _markUp, ...toggledModules } = modules;
        const payload = {
            ...toggledModules,
            [SETTING_PR_REQUIRED_ABOVE]: threshold === '' || threshold === null ? null : Number(threshold),
            [SETTING_PR_EMAIL_ENABLED]: !!prSettings[SETTING_PR_EMAIL_ENABLED],
            [SETTING_PR_EMAIL_RECIPIENTS]: prSettings[SETTING_PR_EMAIL_RECIPIENTS],
            [SETTING_EA_EMAIL_ENABLED]: !!prSettings[SETTING_EA_EMAIL_ENABLED],
            [SETTING_EA_EMAIL_RECIPIENTS]: prSettings[SETTING_EA_EMAIL_RECIPIENTS],
            [SETTING_DC_EMAIL_ENABLED]: !!prSettings[SETTING_DC_EMAIL_ENABLED],
            [SETTING_DC_EMAIL_RECIPIENTS]: prSettings[SETTING_DC_EMAIL_RECIPIENTS],
        };
        axios
            .put(props.toggleEventModuleUrl, {
                ...payload,
                supplier_request_recipients: parseRecipients(supplierRequestRecipients),
            })
            .then(() => {
                setRequest(false);
                toast.success($t('Modules status updated successfully'));
                fetchDataset();
            })
            .catch((e) => {
                setRequest(false);
                toast.error($t('An error occurred, please try again'));
            });
    };

    const toggleSuperUserModule = () => {
        setRequest(true);
        axios
            .put(props.toggleEventModuleUrl, {
                sport_enabled: modules[MODULE_SPORT],
                event_profit_enabled: modules[MODULE_EVENT_PROFIT],
                embedded_source_linking_enabled: modules[MODULE_EMBEDDED_SOURCE_LINKING],
            })
            .then(() => {
                setRequest(false);
                toast.success($t('Modules status updated successfully'));
                fetchDataset();
            })
            .catch((e) => {
                setRequest(false);
                toast.error($t('An error occurred, please try again'));
            });
    };

    const updateEvent = () => {
        setRequest(true);
        const data = {
            name: datasets.event.name,
            currency_id: datasets.event.currency_id,
            locale: datasets.event.locale,
            start_date: datasets.event.start_date ? new Date(datasets.event.start_date).toISOString().split('T')[0] : '',
            event_start_date: datasets.event.event_start_date ? new Date(datasets.event.event_start_date).toISOString().split('T')[0] : '',
            event_end_date: datasets.event.event_end_date ? new Date(datasets.event.event_end_date).toISOString().split('T')[0] : '',
            end_date: datasets.event.end_date ? new Date(datasets.event.end_date).toISOString().split('T')[0] : '',
            cashflow_date: datasets.event.cashflow_date ? new Date(datasets.event.cashflow_date).toISOString().split('T')[0] : '',
        };

        const shouldReloadPage = !datasets.event.set_up_date;

        axios
            .put(updateUrl, data)
            .then((response) => {
                setRequest(false);
                toast.success($t('The event updated successfully'));

                // The save above may have re-slugged the event (see the
                // fetchUrl/updateUrl declaration), which would 404 the
                // refetch below if it still targeted the old slug. Rebuild
                // both URLs from the response before refetching.
                const oldSlug = datasets.event?.slug;
                const newSlug = response.data.event?.slug;
                let nextFetchUrl = fetchUrl;
                if (oldSlug && newSlug && oldSlug !== newSlug) {
                    const rewriteSlug = (url) => url.replace(`/${oldSlug}/`, `/${newSlug}/`);
                    nextFetchUrl = rewriteSlug(fetchUrl);
                    setFetchUrl(nextFetchUrl);
                    setUpdateUrl(rewriteSlug(updateUrl));
                    window.history.replaceState(null, '', rewriteSlug(window.location.pathname) + window.location.search);
                }

                fetchDataset(nextFetchUrl);
                if (shouldReloadPage) {
                    window.location.reload();
                }
            })
            .catch((e) => {
                setRequest(false);
                let message = '';
                Object.keys(e.response.data.errors).forEach((key) => {
                    const errors = e.response.data.errors[key];
                    console.log(errors);

                    errors.forEach((error) => {
                        message += error;
                        message += '<br>';
                    });
                });

                toast.error(message);
            });
    };

    const toggleCollapsed = (type) => {
        setCollapsed((oldCollapsed) => ({ ...oldCollapsed, [type]: !oldCollapsed[type] }));
    };

    const updatePrSetting = (key, value) => {
        setPrSettings((prev) => ({ ...prev, [key]: value }));
    };

    const onToggleModule = (moduleType, value) => {
        if (moduleType === MODULE_VIK && value === 'no' && event.is_vik_disable_restricted && vikCanBeEnabled) {
            confirmDisableVikModule();
            return;
        }

        if (moduleType === MODULE_VIK && !vikCanBeEnabled && value === 'yes') {
            return;
        }

        setModules((oldModule) => ({ ...oldModule, [moduleType]: value === 'yes' }));
    };

    const onToggleEmbeddedSourceLinking = (value) => {
        const isCurrentlyEnabled = !!modules[MODULE_EMBEDDED_SOURCE_LINKING];
        const isEnabling = value === 'yes';

        if (isCurrentlyEnabled && !isEnabling) {
            // Disabling: warn about permanent link breakage
            const confirmed = window.confirm(
                $t(
                    'Disabling will permanently break all existing quantity links. Linked values will freeze at their current amounts. You will need to re-link them manually if re-enabled.',
                ),
            );
            if (!confirmed) return;
        } else if (!isCurrentlyEnabled && isEnabling) {
            // Re-enabling: warn that previous links are not restored
            window.confirm($t('Enabling source linking does not restore previously broken links. You will need to set up new links.'));
            // Proceed regardless — this is just an informational message
        }

        setModules((oldModule) => ({ ...oldModule, [MODULE_EMBEDDED_SOURCE_LINKING]: isEnabling }));
    };

    const changedateDate = (name, e) => {
        const data = JSON.parse(JSON.stringify(datasets));
        if (name === 'cashflowDate') {
            data.event.cashflow_date = e;
        }
        if (name === 'startDate') {
            data.event.start_date = e;
            if (e > data.event.end_date) {
                data.event.end_date = e;
            }
        }
        if (name === 'endDate') {
            data.event.end_date = e;
            if (e < data.event.start_date) {
                data.event.start_date = e;
            }
        }
        if (name === 'eventStartDate') {
            data.event.event_start_date = e;
            if (e > data.event.event_end_date) {
                data.event.event_end_date = e;
            }
        }
        if (name === 'eventEndDate') {
            data.event.event_end_date = e;
            if (e < data.event.event_start_date) {
                data.event.event_start_date = e;
            }
        }
        setDatasets(data);
    };

    const setEventName = (e) => {
        const data = JSON.parse(JSON.stringify(datasets));
        data.event.name = e.target.value;
        setDatasets(data);
    };

    const setLocale = (e) => {
        const data = JSON.parse(JSON.stringify(datasets));
        data.event.locale = e.target.value;
        setDatasets(data);
    };
    const setCurrency = (e) => {
        const data = JSON.parse(JSON.stringify(datasets));
        data.event.currency_id = e.target.value;
        setDatasets(data);
    };

    return (
        <div>
            <div className="tw-space-y-2">
                <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                    <div>
                        <SectionTile collapsed={collapsed[TYPE_EVENT_SETUP]} header={$t('Settings')} onToggle={() => toggleCollapsed(TYPE_EVENT_SETUP)}>
                            <ContentList>
                                <ContentListItem title={$t('Organisation Name')}>
                                    <div className="tw-flex tw-items-center tw-gap-x-2">
                                        <CircleCheckIcon className="tw-min-w-4 tw-h-4 tw-text-green" />
                                        <div className={'tw-h-8 tw-flex tw-items-center tw-justify-center'}>{datasets.event?.organisation?.name}</div>
                                    </div>
                                </ContentListItem>
                                <ContentListItem title={$t('Project Name')}>
                                    <div className="tw-flex tw-items-center tw-gap-x-2">
                                        {datasets?.event?.name ? (
                                            <CircleCheckIcon className="tw-min-w-4 tw-h-4 tw-text-green" />
                                        ) : (
                                            <CircleXMarkIcon className="tw-min-w-4 tw-h-4 tw-text-red" />
                                        )}
                                        <input
                                            type="text"
                                            className={
                                                datasets?.event && !datasets?.event?.name ? 'tw-border tw-border-red-500 text-input' : 'tw-border-0 text-input'
                                            }
                                            required={true}
                                            placeholder={$t('Event Name')}
                                            value={datasets?.event?.name}
                                            onChange={setEventName}
                                        ></input>
                                    </div>
                                </ContentListItem>
                                <ContentListItem
                                    title={$t('Organisation start date')}
                                    info={$t('The date range selected here is the date range that you will be able to input date specific data against')}
                                >
                                    <div className="tw-flex tw-items-center tw-gap-x-2">
                                        {datasets?.event?.start_date ? (
                                            <CircleCheckIcon className="tw-min-w-4 tw-h-4 tw-text-green" />
                                        ) : (
                                            <CircleXMarkIcon className="tw-min-w-4 tw-h-4 tw-text-red" />
                                        )}
                                        <DatePickerField
                                            name="startDate"
                                            className={'tw-border-0 text-input'}
                                            value={datasets?.event?.start_date}
                                            onChange={changedateDate}
                                            valueFormat={'yyyy-MM-dd'}
                                        />
                                    </div>
                                </ContentListItem>
                                <ContentListItem
                                    title={$t('Organisation end date')}
                                    info={$t('The date range selected here is the date range that you will be able to input date specific data against')}
                                >
                                    <div className="tw-flex tw-items-center tw-gap-x-2">
                                        {datasets?.event?.end_date ? (
                                            <CircleCheckIcon className="tw-min-w-4 tw-h-4 tw-text-green" />
                                        ) : (
                                            <CircleXMarkIcon className="tw-min-w-4 tw-h-4 tw-text-red" />
                                        )}
                                        <DatePickerField
                                            name="endDate"
                                            className={'tw-border-0 text-input'}
                                            value={datasets?.event?.end_date}
                                            onChange={changedateDate}
                                            valueFormat={'yyyy-MM-dd'}
                                        />
                                    </div>
                                </ContentListItem>
                                <ContentListItem
                                    title={$t('Event start date')}
                                    info={$t('The date range selected here is the date range that you will be able to add sessions to your events')}
                                >
                                    <div className="tw-flex tw-items-center tw-gap-x-2">
                                        {datasets?.event?.event_start_date ? (
                                            <CircleCheckIcon className="tw-min-w-4 tw-h-4 tw-text-green" />
                                        ) : (
                                            <CircleXMarkIcon className="tw-min-w-4 tw-h-4 tw-text-red" />
                                        )}
                                        <DatePickerField
                                            name="eventStartDate"
                                            className={'tw-border-0 text-input'}
                                            value={datasets?.event?.event_start_date}
                                            onChange={changedateDate}
                                            valueFormat={'yyyy-MM-dd'}
                                        />
                                    </div>
                                </ContentListItem>
                                <ContentListItem
                                    title={$t('Event end date')}
                                    info={$t('The date range selected here is the date range that you will be able to add sessions to your events')}
                                >
                                    <div className="tw-flex tw-items-center tw-gap-x-2">
                                        {datasets?.event?.event_end_date ? (
                                            <CircleCheckIcon className="tw-min-w-4 tw-h-4 tw-text-green" />
                                        ) : (
                                            <CircleXMarkIcon className="tw-min-w-4 tw-h-4 tw-text-red" />
                                        )}
                                        <DatePickerField
                                            name="eventEndDate"
                                            className={'tw-border-0 text-input'}
                                            value={datasets?.event?.event_end_date}
                                            onChange={changedateDate}
                                            valueFormat={'yyyy-MM-dd'}
                                        />
                                    </div>
                                </ContentListItem>
                                <ContentListItem
                                    title={$t('Default Cashflow Date')}
                                    info={$t(
                                        'This is the date that all revenues and expenditures default to for recording cashflow when items are first added into inbudget',
                                    )}
                                >
                                    <div className="tw-flex tw-items-center tw-gap-x-2">
                                        {datasets?.event?.cashflow_date ? (
                                            <CircleCheckIcon className="tw-min-w-4 tw-h-4 tw-text-green" />
                                        ) : (
                                            <CircleXMarkIcon className="tw-min-w-4 tw-h-4 tw-text-red" />
                                        )}
                                        <DatePickerField
                                            name="cashflowDate"
                                            className={
                                                datasets?.event && !datasets?.event?.cashflow_date
                                                    ? 'tw-border tw-border-red-500 text-input'
                                                    : 'tw-border-0 text-input'
                                            }
                                            value={datasets?.event?.cashflow_date}
                                            onChange={changedateDate}
                                            valueFormat={'yyyy-MM-dd'}
                                        />
                                    </div>
                                </ContentListItem>
                                <ContentListItem title={$t('Project Locale')}>
                                    <div className="tw-flex tw-items-center tw-gap-x-2">
                                        {datasets?.event?.locale ? (
                                            <CircleCheckIcon className="tw-min-w-4 tw-h-4 tw-text-green" />
                                        ) : (
                                            <CircleXMarkIcon className="tw-min-w-4 tw-h-4 tw-text-red" />
                                        )}
                                        <SelectInput horizontalLayout={true} parentGridClass={'tw-grow'} onChange={setLocale} value={datasets?.event?.locale}>
                                            <option value={''}>{$t('Select Locale')}</option>
                                            {props.locales.map((locale) => {
                                                return (
                                                    <option key={locale.code} value={locale.code}>
                                                        {locale.name}
                                                    </option>
                                                );
                                            })}
                                        </SelectInput>
                                    </div>
                                </ContentListItem>
                                <ContentListItem title={$t('Local Currency')}>
                                    <div className="tw-flex tw-items-center tw-gap-x-2">
                                        {datasets?.event?.currency_id ? (
                                            <CircleCheckIcon className="tw-min-w-4 tw-h-4 tw-text-green" />
                                        ) : (
                                            <CircleXMarkIcon className="tw-min-w-4 tw-h-4 tw-text-red" />
                                        )}
                                        <SelectInput
                                            horizontalLayout={true}
                                            parentGridClass={'tw-grow'}
                                            onChange={setCurrency}
                                            value={datasets?.event?.currency_id}
                                            disabled={datasets?.currency_locked}
                                        >
                                            <option value={''}>{$t('Select Currency')}</option>
                                            {props.currencies.map((currency) => {
                                                return (
                                                    <option key={currency.id} value={currency.id}>
                                                        {currency.code} - {currency.name}
                                                    </option>
                                                );
                                            })}
                                        </SelectInput>
                                    </div>
                                    {datasets?.currency_locked && (
                                        <p className="tw-mt-1.5 tw-text-2xs tw-text-charcoal-50">
                                            {$t('The base currency for all budgets and reports. It cannot be changed once figures exist.')}
                                        </p>
                                    )}
                                </ContentListItem>
                            </ContentList>
                            {showWarning && (
                                <Alert className="tw-my-2" type="warning">
                                    {$t(
                                        'You are changing the organisational dates, which may mean you have records that fall outside of these new dates. Would you like to proceed?',
                                    )}
                                </Alert>
                            )}
                            <div className="tw-self-end tw-mt-auto">
                                <Button variant="primary" onClick={updateEvent} disabled={request}>
                                    {$t('Save')}
                                </Button>
                            </div>
                        </SectionTile>
                    </div>
                    <div>
                        <SectionTile collapsed={collapsed[TYPE_MODULES]} header={$t('Modules')} onToggle={() => toggleCollapsed(TYPE_MODULES)}>
                            <div className="tw-flex tw-flex-col tw-gap-y-3">
                                {!!datasets.module_cards && (
                                    <ModuleTogglesPanel
                                        key={datasets.modulesUpdateUrl}
                                        cards={datasets.module_cards}
                                        canToggle={!!canToggleModules}
                                        updateUrl={datasets.modulesUpdateUrl}
                                        // Patches only what a module save changes, so unsaved
                                        // edits in the other sections survive it.
                                        onSaved={(data) =>
                                            setDatasets((previous) => ({
                                                ...previous,
                                                module_cards: data.module_cards,
                                                event: { ...previous.event, workforce_enabled: data.workforce_enabled },
                                            }))
                                        }
                                    />
                                )}
                                {datasets.canManageSectionVisibility && !!datasets.sectionVisibilityUrl && (
                                    <SectionVisibilityPanel
                                        key={datasets.sectionVisibilityUrl}
                                        sections={datasets.sections}
                                        updateUrl={datasets.sectionVisibilityUrl}
                                    />
                                )}
                            </div>
                        </SectionTile>
                    </div>
                    <div>
                        <SectionTile
                            collapsed={collapsed[TYPE_EVENT_FUNCTIONALITY]}
                            header={$t('Functionality')}
                            onToggle={() => toggleCollapsed(TYPE_EVENT_FUNCTIONALITY)}
                        >
                            <ContentList>
                                <SelectInput
                                    label={$t('Turn on Ticketing')}
                                    onChange={(e) => onToggleModule(MODULE_TICKETING, e.target.value)}
                                    value={modules[MODULE_TICKETING] ? 'yes' : 'no'}
                                    infoBackgroundClass="tw-bg-charcoal"
                                    info={$t(
                                        'Turn on ticketing to access the inbudget ticketing model. This will appear under the Revenue main menu sub-heading within the Budget module. The ticketing model uses information loaded for venues (capacities) and events (sessions) to automatically forecast ticket revenue. You can customise your assumptions for prices, sales percentages and seat kills / holds for each event or session in the model to forecast your ticketing revenue.',
                                    )}
                                >
                                    <option value="yes">{$t('Yes')}</option>
                                    <option value="no">{$t('No')}</option>
                                </SelectInput>
                                <SelectInput
                                    label={$t('Turn on Sponsorship')}
                                    onChange={(e) => onToggleModule(MODULE_SPONSORSHIP, e.target.value)}
                                    value={modules[MODULE_SPONSORSHIP] ? 'yes' : 'no'}
                                    infoBackgroundClass="tw-bg-charcoal"
                                    info={$t(
                                        'Turn on sponsorship to access the inbudget sponsorship model. Create sponsorship tiers and capture individual sponsors including how much revenue you are forecasting for each one.',
                                    )}
                                >
                                    <option value="yes">{$t('Yes')}</option>
                                    <option value="no">{$t('No')}</option>
                                </SelectInput>
                                {props.enabledModuleVik && (
                                    <SelectInput
                                        label={$t('Turn on VIK')}
                                        onChange={(e) => onToggleModule(MODULE_VIK, e.target.value)}
                                        value={modules[MODULE_VIK] ? 'yes' : 'no'}
                                        infoBackgroundClass="tw-bg-charcoal"
                                        info={$t(
                                            'Turn on Value in Kind (VIK) functionality to capture revenues and expenditures by cash and VIK. VIK is shown as target VIK and committed VIK. Target VIK is used to identify VIK opportunities that are to be targeted prior to it being actually secured from a sponsor, whereas Committed VIK shows what is contracted after signing a deal with a sponsor. You can see how much revenue that is captured as VIK has been allocated on the expenditure side for each individual sponsor using the bespoke VIK report found in the Report Centre.',
                                        )}
                                        disabled={!vikCanBeEnabled}
                                    >
                                        <option value="yes">{$t('Yes')}</option>
                                        <option value="no">{$t('No')}</option>
                                    </SelectInput>
                                )}

                                <SelectInput
                                    label={$t('Turn on Budget Change Management and Contingencies Functionality')}
                                    onChange={(e) => onToggleModule(MODULE_CONTINGENCY, e.target.value)}
                                    value={modules[MODULE_CONTINGENCY] ? 'yes' : 'no'}
                                    infoBackgroundClass="tw-bg-charcoal"
                                    info={$t(
                                        'Turn on this feature to be able to create a central contingency and allocate contingency to departments / cost centres.  Contingencies can be a useful tool for budget change management.',
                                    )}
                                    selectInfo={
                                        props.contingencyCanBeDisabled
                                            ? ''
                                            : $t(
                                                  'To turn off this functionality, you must first remove all contingency balances and deactivate any active budget change management',
                                              )
                                    }
                                    disabled={!props.contingencyCanBeDisabled}
                                >
                                    <option value="yes">{$t('Yes')}</option>
                                    <option value="no">{$t('No')}</option>
                                </SelectInput>
                                <SelectInput
                                    label={$t('Turn on Cost Templates')}
                                    onChange={(e) => onToggleModule(MODULE_COST_TEMPLATE, e.target.value)}
                                    value={modules[MODULE_COST_TEMPLATE] ? 'yes' : 'no'}
                                    infoBackgroundClass="tw-bg-charcoal"
                                    info={$t(
                                        'Turn cost templates on to have the ability to create a template of items that you can then assign quantities to for each venue in your project',
                                    )}
                                    selectInfo={
                                        !props.costTemplateCanBeDisabled &&
                                        $t('Please remove cost templates from the latest estimates to be able to turn them off.')
                                    }
                                    disabled={!props.costTemplateCanBeDisabled}
                                >
                                    <option value="yes">{$t('Yes')}</option>
                                    <option value="no">{$t('No')}</option>
                                </SelectInput>
                                <SelectInput
                                    label={$t('Turn on Cost Packs')}
                                    onChange={(e) => onToggleModule(MODULE_COST_PACK, e.target.value)}
                                    value={modules[MODULE_COST_PACK] ? 'yes' : 'no'}
                                    infoBackgroundClass="tw-bg-charcoal"
                                    info={$t(
                                        'Turn cost packs on to have the ability to create a cost packs that you can then assign to venue days.  Contractor cost packs enable you to allocate contractors to venues by day, capturing hours for each contractor on each day and automatically calculating numbers of shifts based on parameters you input. Find out more in the User guide.',
                                    )}
                                    selectInfo={
                                        !props.costPackCanBeDisabled && $t('Please remove cost pack allocations to venues to be able to turn them off.')
                                    }
                                    disabled={!props.costPackCanBeDisabled}
                                >
                                    <option value="yes">{$t('Yes')}</option>
                                    <option value="no">{$t('No')}</option>
                                </SelectInput>
                                <SelectInput
                                    label={$t('Turn on Departmental Project Plans')}
                                    onChange={(e) => onToggleModule(MODULE_DEPARTMENTAL_PROJECT_PLANS, e.target.value)}
                                    value={modules[MODULE_DEPARTMENTAL_PROJECT_PLANS] ? 'yes' : 'no'}
                                    infoBackgroundClass="tw-bg-charcoal"
                                    info={$t(
                                        'Enable project plans at department level, so each functional area can manage its own tasks, milestones and timelines independently of the central project plan and venue project plans.',
                                    )}
                                >
                                    <option value="yes">{$t('Yes')}</option>
                                    <option value="no">{$t('No')}</option>
                                </SelectInput>
                                <SelectInput
                                    label={$t('Turn on Venue Project Plans')}
                                    onChange={(e) => onToggleModule(MODULE_VENUE_PROJECT_PLANS, e.target.value)}
                                    value={modules[MODULE_VENUE_PROJECT_PLANS] ? 'yes' : 'no'}
                                    infoBackgroundClass="tw-bg-charcoal"
                                    info={$t(
                                        'Enable project plans at venue level, so venue teams can manage tasks, milestones and timelines specific to each venue.',
                                    )}
                                >
                                    <option value="yes">{$t('Yes')}</option>
                                    <option value="no">{$t('No')}</option>
                                </SelectInput>
                                <SelectInput
                                    label={$t('Turn on Venue R.A.I.D')}
                                    onChange={(e) => onToggleModule(MODULE_VENUE_RAID, e.target.value)}
                                    value={modules[MODULE_VENUE_RAID] ? 'yes' : 'no'}
                                    infoBackgroundClass="tw-bg-charcoal"
                                    info={$t(
                                        'Enable a venue-level register of Risks, Assumptions, Issues and Dependencies, captured and managed separately from the project-wide R.A.D.I.O. log.',
                                    )}
                                >
                                    <option value="yes">{$t('Yes')}</option>
                                    <option value="no">{$t('No')}</option>
                                </SelectInput>
                                <SelectInput
                                    label={$t('Show procurements at department level')}
                                    onChange={(e) => onToggleModule(MODULE_DEPT_PROCUREMENT_PLAN, e.target.value)}
                                    value={modules[MODULE_DEPT_PROCUREMENT_PLAN] ? 'yes' : 'no'}
                                    infoBackgroundClass="tw-bg-charcoal"
                                    info={$t(
                                        'Display procurement aggregated at the department level rather than only in the central procurement module. Enable department owners to instigate PRs and EAs from the department project plan.',
                                    )}
                                >
                                    <option value="yes">{$t('Yes')}</option>
                                    <option value="no">{$t('No')}</option>
                                </SelectInput>
                                <SelectInput
                                    label={$t('Turn on Supplier Portal')}
                                    onChange={(e) => onToggleModule(MODULE_SUPPLIER_PORTAL, e.target.value)}
                                    value={modules[MODULE_SUPPLIER_PORTAL] ? 'yes' : 'no'}
                                    infoBackgroundClass="tw-bg-charcoal"
                                    info={$t(
                                        'Enable external suppliers to respond to your document requests and manage their submissions directly, reducing manual handling by the event team — e.g. request and enable suppliers to upload their insurance certificates, or support a contractual deliverable.',
                                    )}
                                >
                                    <option value="yes">{$t('Yes')}</option>
                                    <option value="no">{$t('No')}</option>
                                </SelectInput>
                                {modules[SECTION_PROCUREMENT_EVALUATION] !== undefined && (
                                    <SelectInput
                                        label={$t('Turn on Procurement Evaluation')}
                                        onChange={(e) => onToggleModule(SECTION_PROCUREMENT_EVALUATION, e.target.value)}
                                        value={modules[SECTION_PROCUREMENT_EVALUATION] ? 'yes' : 'no'}
                                        infoBackgroundClass="tw-bg-charcoal"
                                        info={$t('Score and compare supplier submissions against weighted evaluation criteria before you award a contract.')}
                                    >
                                        <option value="yes">{$t('Yes')}</option>
                                        <option value="no">{$t('No')}</option>
                                    </SelectInput>
                                )}
                            </ContentList>
                            <div className="tw-flex tw-justify-end  tw-mt-2">
                                <Button variant="primary" onClick={toggleModule} disabled={request}>
                                    {$t('Save')}
                                </Button>
                            </div>
                        </SectionTile>
                    </div>
                    <div>
                        <SectionTile
                            collapsed={collapsed[TYPE_COMMITMENT_SETTINGS]}
                            header={$t('Commitment Settings')}
                            onToggle={() => toggleCollapsed(TYPE_COMMITMENT_SETTINGS)}
                        >
                            <ContentList>
                                {props.enabledModuleApprovals && (
                                    <>
                                        <SelectInput
                                            label={$t('Turn on Purchase Requisitions')}
                                            onChange={(e) => onToggleModule(MODULE_PR, e.target.value)}
                                            value={modules[MODULE_PR] ? 'yes' : 'no'}
                                            infoBackgroundClass="tw-bg-charcoal"
                                            info={$t(
                                                'Turn on this feature to enable users to create Purchase Requisitions (PRs). The PR indicates that they want to start a procurement process and, in the Approval Management section, users can be set as Approvers - to approve or reject these PRs.',
                                            )}
                                        >
                                            <option value="yes">{$t('Yes')}</option>
                                            <option value="no">{$t('No')}</option>
                                        </SelectInput>
                                        <SelectInput
                                            label={$t('Turn on Expenditure Approvals')}
                                            onChange={(e) => onToggleModule(MODULE_EA, e.target.value)}
                                            value={modules[MODULE_EA] ? 'yes' : 'no'}
                                            infoBackgroundClass="tw-bg-charcoal"
                                            info={$t(
                                                'Turn on this feature to enable users to create Expenditure Approvals (EAs). An EA can be used to gain approval to commit an expenditure, once a procurement process has been completed. When the EA is completed, the budget line item moves from being shown as uncommitted, to committed – enabling the user to see their committed and uncommitted expenditure position across the event. In the Approval Management section, users can be set as Approvers – to approve or reject these EAs.',
                                            )}
                                        >
                                            <option value="yes">{$t('Yes')}</option>
                                            <option value="no">{$t('No')}</option>
                                        </SelectInput>

                                        {!!modules[MODULE_PR] && (
                                            <FormInput
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                label={$t('PR required above ({{currency}})', {
                                                    currency: event?.currency?.code || event?.currency_code || event?.currency?.symbol || '',
                                                })}
                                                value={prSettings[SETTING_PR_REQUIRED_ABOVE] ?? ''}
                                                placeholder={$t('Leave blank for no threshold')}
                                                onChange={(e) => updatePrSetting(SETTING_PR_REQUIRED_ABOVE, e.target.value)}
                                            />
                                        )}

                                        <SelectInput
                                            label={$t('Email approved PRs to requestor and approvers')}
                                            onChange={(e) => updatePrSetting(SETTING_PR_EMAIL_ENABLED, e.target.value === 'yes')}
                                            value={prSettings[SETTING_PR_EMAIL_ENABLED] ? 'yes' : 'no'}
                                        >
                                            <option value="yes">{$t('Yes')}</option>
                                            <option value="no">{$t('No')}</option>
                                        </SelectInput>
                                        {!!prSettings[SETTING_PR_EMAIL_ENABLED] && (
                                            <div className="tw-space-y-1.5">
                                                <label className="tw-text-charcoal tw-text-sm tw-mb-0">{$t('Additional PR recipients')}</label>
                                                <EmailRecipientsInput
                                                    value={prSettings[SETTING_PR_EMAIL_RECIPIENTS]}
                                                    onChange={(emails) => updatePrSetting(SETTING_PR_EMAIL_RECIPIENTS, emails)}
                                                    placeholder={$t('Add email addresses')}
                                                />
                                            </div>
                                        )}

                                        <SelectInput
                                            label={$t('Email approved EAs to requestor and approvers')}
                                            onChange={(e) => updatePrSetting(SETTING_EA_EMAIL_ENABLED, e.target.value === 'yes')}
                                            value={prSettings[SETTING_EA_EMAIL_ENABLED] ? 'yes' : 'no'}
                                        >
                                            <option value="yes">{$t('Yes')}</option>
                                            <option value="no">{$t('No')}</option>
                                        </SelectInput>
                                        {!!prSettings[SETTING_EA_EMAIL_ENABLED] && (
                                            <div className="tw-space-y-1.5">
                                                <label className="tw-text-charcoal tw-text-sm tw-mb-0">{$t('Additional EA recipients')}</label>
                                                <EmailRecipientsInput
                                                    value={prSettings[SETTING_EA_EMAIL_RECIPIENTS]}
                                                    onChange={(emails) => updatePrSetting(SETTING_EA_EMAIL_RECIPIENTS, emails)}
                                                    placeholder={$t('Add email addresses')}
                                                />
                                            </div>
                                        )}

                                        <SelectInput
                                            label={$t('Email approved Direct Commitments to requestor')}
                                            onChange={(e) => updatePrSetting(SETTING_DC_EMAIL_ENABLED, e.target.value === 'yes')}
                                            value={prSettings[SETTING_DC_EMAIL_ENABLED] ? 'yes' : 'no'}
                                        >
                                            <option value="yes">{$t('Yes')}</option>
                                            <option value="no">{$t('No')}</option>
                                        </SelectInput>
                                        {!!prSettings[SETTING_DC_EMAIL_ENABLED] && (
                                            <div className="tw-space-y-1.5">
                                                <label className="tw-text-charcoal tw-text-sm tw-mb-0">{$t('Additional DC recipients')}</label>
                                                <EmailRecipientsInput
                                                    value={prSettings[SETTING_DC_EMAIL_RECIPIENTS]}
                                                    onChange={(emails) => updatePrSetting(SETTING_DC_EMAIL_RECIPIENTS, emails)}
                                                    placeholder={$t('Add email addresses')}
                                                />
                                            </div>
                                        )}
                                    </>
                                )}

                                <div className="tw-space-y-2">
                                    <SelectInput
                                        label={$t('Turn on Direct Expenditure Commitment')}
                                        onChange={(e) => onToggleModule(MODULE_EXPENSE_DIRECT_COMMIT, e.target.value)}
                                        value={modules[MODULE_EXPENSE_DIRECT_COMMIT] ? 'yes' : 'no'}
                                        infoBackgroundClass="tw-bg-charcoal"
                                        info={$t(
                                            'Turn on Direct Expenditure Commitments (DC) to enable users to be able to directly commit expenditures without raising purchase requisitions and expenditure approvals. Direct Commitments (DCs) still go through the same approval process you set up for EAs.',
                                        )}
                                    >
                                        <option value="yes">{$t('Yes')}</option>
                                        <option value="no">{$t('No')}</option>
                                    </SelectInput>
                                </div>

                                <div className="tw-space-y-2">
                                    <SelectInput
                                        label={$t('Turn on Direct Revenue Commitment')}
                                        onChange={(e) => onToggleModule(MODULE_REVENUE_DIRECT_COMMIT, e.target.value)}
                                        value={modules[MODULE_REVENUE_DIRECT_COMMIT] ? 'yes' : 'no'}
                                        infoBackgroundClass="tw-bg-charcoal"
                                        info={$t(
                                            'Turn on Direct Revenue Commitments (DC) to enable users to be able to directly commit revenues. Once a revenue commitment is completed, the budgeted revenue line moves from being shown as uncommitted, to committed – enabling the user to see their committed and uncommitted revenue position across the event.',
                                        )}
                                    >
                                        <option value="yes">{$t('Yes')}</option>
                                        <option value="no">{$t('No')}</option>
                                    </SelectInput>
                                </div>
                                {!!props.isSuperAdmin && (
                                    <>
                                        {/* Temporarily hiding this toggle until finalizing its proper usage */}
                                        {/* <SelectInput
                                            label={$t('Allow commitments to exceed budgets')}
                                            onChange={(e) => onToggleModule(MODULE_COMMIT_EXCEED_BUDGET, e.target.value)}
                                            value={modules[MODULE_COMMIT_EXCEED_BUDGET] ? 'yes' : 'no'}
                                        >
                                            <option value="yes">{$t('Yes')}</option>
                                            <option value="no">{$t('No')}</option>
                                        </SelectInput> */}
                                    </>
                                )}
                            </ContentList>
                            <div className="tw-mt-4">
                                <label htmlFor="supplier_request_recipients" className="text-input__label">
                                    {$t('Supplier request recipients')}
                                </label>
                                <input
                                    id="supplier_request_recipients"
                                    type="text"
                                    className="tw-block tw-w-full tw-rounded-lg tw-text-sm tw-border-grey tw-text-charcoal"
                                    placeholder={$t('procurement@example.com, finance@example.com')}
                                    value={supplierRequestRecipients}
                                    onChange={(e) => setSupplierRequestRecipients(e.target.value)}
                                />
                                <p className="tw-text-xs tw-text-charcoal/70 tw-mt-1">
                                    {$t(
                                        'These addresses are emailed when a user requests a new supplier be added to the ERP. Separate multiple addresses with a comma.',
                                    )}
                                </p>
                            </div>
                            <div className="tw-flex tw-justify-end  tw-mt-2">
                                <Button variant="primary" onClick={toggleModule} disabled={request}>
                                    {$t('Save')}
                                </Button>
                            </div>
                        </SectionTile>
                    </div>
                    {props.canManageStatusRules && props.statusRulesUrl && (
                        <div className="tw-col-span-2">
                            <StatusRulesCard url={props.statusRulesUrl} />
                        </div>
                    )}
                    {!!props.isSuperAdmin && (
                        <div>
                            <SectionTile collapsed={collapsed[TYPE_SUPER_USER]} header={$t('Super User')} onToggle={() => toggleCollapsed(TYPE_SUPER_USER)}>
                                <ContentList>
                                    {(props.enabledModuleSportDepartment || props.isEventCashflowDateSet) && (
                                        <>
                                            <div className={'tw-flex tw-gap-2'}>
                                                <label className={'tw-text-charcoal tw-text-sm tw-mb-0'}>{$t('Turn on Sport Department')}</label>
                                                {!props.isModuleSportDepartmentChangeable && (
                                                    <button type="button" ref={setTriggerRef}>
                                                        <InformationCircleIcon className="tw-mt-0 tw-mb-0 tw-p-0 tw-cursor-pointer" />
                                                    </button>
                                                )}
                                            </div>
                                            <SelectInput
                                                onChange={(e) => onToggleModule(MODULE_SPORT, e.target.value)}
                                                value={modules[MODULE_SPORT] ? 'yes' : 'no'}
                                                disabled={!props.isModuleSportDepartmentChangeable}
                                            >
                                                <option value="yes">{$t('Yes')}</option>
                                                <option value="no">{$t('No')}</option>
                                            </SelectInput>
                                            {!!visible && (
                                                <TileTooltip
                                                    ref={setTooltipRef}
                                                    tooltipText={$t(
                                                        props.enabledModuleSportDepartment
                                                            ? 'Unable to turn off the Sport department when balances exist. Please remove all balances to be able to turn off the Sport Department'
                                                            : 'Unable to turn on the Sport department because cashflow date for the event has been specified yet',
                                                    )}
                                                    getTooltipProps={getTooltipProps}
                                                    getArrowProps={getArrowProps}
                                                />
                                            )}
                                        </>
                                    )}
                                    <SelectInput
                                        label={$t('Allow Event Profit to be used to fund budget increases where contingency is not sufficient or activated')}
                                        onChange={(e) => onToggleModule(MODULE_EVENT_PROFIT, e.target.value)}
                                        value={modules[MODULE_EVENT_PROFIT] ? 'yes' : 'no'}
                                    >
                                        <option value="yes">{$t('Yes')}</option>
                                        <option value="no">{$t('No')}</option>
                                    </SelectInput>
                                    <SelectInput
                                        label={$t('Enable Embedded Source Linking')}
                                        onChange={(e) => onToggleEmbeddedSourceLinking(e.target.value)}
                                        value={modules[MODULE_EMBEDDED_SOURCE_LINKING] ? 'yes' : 'no'}
                                        infoBackgroundClass="tw-bg-charcoal"
                                        info={$t(
                                            'Enable this feature to allow expense quantity fields to be linked to revenue line totals. When the source changes, linked expense quantities update automatically.',
                                        )}
                                    >
                                        <option value="yes">{$t('Yes')}</option>
                                        <option value="no">{$t('No')}</option>
                                    </SelectInput>
                                </ContentList>
                                <div className="tw-flex tw-justify-end  tw-mt-2">
                                    <Button variant="primary" onClick={toggleSuperUserModule} disabled={request}>
                                        {$t('Save')}
                                    </Button>
                                </div>
                            </SectionTile>
                        </div>
                    )}

                    {/* Only where the organisation has time keeping switched on —
                        without the module there is nothing for a lock date to close. */}
                    {props.timeKeepingEnabled && props.canManageTimeLock && props.timeLockUpdateUrl && (
                        <div>
                            <TimeLockCard lockedUntil={props.timeLockedUntil} updateUrl={props.timeLockUpdateUrl} />
                        </div>
                    )}
                    <div>
                        <SectionTile collapsed={collapsed[TYPE_VENUES]} header={$t('Venues')} onToggle={() => toggleCollapsed(TYPE_VENUES)}>
                            <ContentList>
                                <SummaryItem title={$t('Adding a Venue')} completed={datasets.venues?.completed} link={datasets.venues?.link} />
                                <SummaryItem title={$t('Adding an Event to a Venue')} completed={datasets.venues_sports?.completed} />
                            </ContentList>
                        </SectionTile>
                    </div>
                    <div>
                        <SectionTile collapsed={collapsed[TYPE_SPORTS]} header={$t('Event')} onToggle={() => toggleCollapsed(TYPE_SPORTS)}>
                            <SummaryItem title={$t('Adding an Event')} completed={datasets.sports?.completed} link={datasets.sports?.link} />
                            <SummaryItem title={$t('Adding a Session to an Event')} completed={datasets.sports_sessions?.completed} />
                        </SectionTile>
                    </div>
                    <div>
                        <SectionTile collapsed={collapsed[TYPE_DEP_CC]} header={$t('Departments & Cost Centres')} onToggle={() => toggleCollapsed(TYPE_DEP_CC)}>
                            <SummaryItem
                                title={$t('Adding a Revenue Department')}
                                completed={datasets.dep_cc_revenues?.completed}
                                link={datasets.dep_cc_revenues?.link}
                            />
                            <SummaryItem
                                title={$t('Adding an Expenditure Department')}
                                completed={datasets.dep_cc_expenditures?.completed}
                                link={datasets.dep_cc_expenditures?.link}
                            />
                        </SectionTile>
                    </div>
                    {!!datasets.event?.workforce_enabled && (
                        <div>
                            <SectionTile collapsed={collapsed[TYPE_WORKFORCE]} header={$t('Workforce')} onToggle={() => toggleCollapsed(TYPE_WORKFORCE)}>
                                <SummaryItem
                                    title={$t('Adding Workforce Assumptions')}
                                    completed={datasets.workforce_assumptions?.completed}
                                    link={datasets.workforce_assumptions?.link}
                                />
                                <SummaryItem
                                    title={$t('Adding Pay Rates')}
                                    completed={datasets.workforce_payrates?.completed}
                                    link={datasets.workforce_payrates?.link}
                                />
                            </SectionTile>
                        </div>
                    )}
                    <div>
                        <SectionTile collapsed={collapsed[TYPE_PROCUREMENTS]} header={$t('Procurement')} onToggle={() => toggleCollapsed(TYPE_PROCUREMENTS)}>
                            <SummaryItem
                                title={$t('Adding your Procurement Policy')}
                                completed={datasets.procurements_policies?.completed}
                                link={datasets.procurements_policies?.link}
                            />
                            <SummaryItem
                                title={$t('Adding Procurement Categories')}
                                completed={datasets.procurements_categories?.completed}
                                link={datasets.procurements_categories?.link}
                            />
                            <SummaryItem
                                title={$t('Adding Approvals for Purchase Requisitions')}
                                completed={datasets.procurements_pr_approvals?.completed}
                                link={datasets.procurements_pr_approvals?.link}
                            />
                            <SummaryItem
                                title={$t('Adding Approvals for Expenditure Approvals')}
                                completed={datasets.procurements_po_approvals?.completed}
                                link={datasets.procurements_po_approvals?.link}
                            />
                        </SectionTile>
                    </div>
                    <div>
                        <SectionTile collapsed={collapsed[TYPE_RISKS]} header={$t('R.A.D.I.O.')} onToggle={() => toggleCollapsed(TYPE_RISKS)}>
                            <SummaryItem title={$t('Adding Risks Types')} completed={datasets.risks_types?.completed} link={datasets.risks_types?.link} />
                            <SummaryItem
                                title={$t('Adding Impact Categories')}
                                completed={datasets.risks_impact_categories?.completed}
                                link={datasets.risks_impact_categories?.link}
                            />
                            <SummaryItem
                                title={$t('Adding Status Categories')}
                                completed={datasets.risks_statuses?.completed}
                                link={datasets.risks_statuses?.link}
                            />
                        </SectionTile>
                    </div>
                </div>
            </div>
        </div>
    );
}

const TileTooltip = forwardRef(function TileTooltip({ tooltipText, getTooltipProps, getArrowProps }, ref) {
    return (
        <div
            ref={ref}
            data-popper-reference-hidden="true"
            {...getTooltipProps({
                className: 'tw-flex tw-flex-col tw-gap-y-2 tw-bg-blue tw-p-2.5 tw-rounded-lg tw-border-0 tw-text-xs tw-text-white',
                style: { maxWidth: '20rem' },
            })}
        >
            {tooltipText}
        </div>
    );
});

function SummaryItem({ title, completed, link }) {
    return (
        <ContentListItem title={title}>
            <div className="tw-flex tw-justify-end">
                <div className="tw-grid tw-grid-cols-2 tw-items-center tw-justify-end tw-gap-x-2">
                    {completed ? <CircleCheckIcon className="tw-w-5 tw-h-5 tw-text-green" /> : <HourglassIcon className="tw-w-5 tw-h-5 tw-text-charcoal" />}
                    {link && (
                        <a href={link} target="_blank" rel="noreferrer">
                            <LinkIcon className="tw-min-w-4 tw-h-4 tw-text-blue" />
                        </a>
                    )}
                </div>
            </div>
        </ContentListItem>
    );
}

if (document.getElementById('EventSetup')) {
    var rootEl = document.getElementById('EventSetup');
    ReactDOM.render(
        <App>
            <ConfirmationModalProvider>
                <EventSetup
                    fetchEventSetupUrl={fetchEventSetupUrl}
                    toggleEventModuleUrl={toggleEventModuleUrl}
                    locales={locales}
                    currencies={currencies}
                    updateEventUrl={updateEventUrl}
                    enabledModuleSportDepartment={enabledModuleSportDepartment}
                    isEventCashflowDateSet={isEventCashflowDateSet}
                    isModuleSportDepartmentChangeable={isModuleSportDepartmentChangeable}
                    enabledModuleVik={enabledModuleVik}
                    enabledModuleApprovals={enabledModuleApprovals}
                    isSuperAdmin={isSuperAdmin}
                    contingencyCanBeDisabled={contingencyCanBeDisabled}
                    costTemplateCanBeDisabled={costTemplateCanBeDisabled}
                    costPackCanBeDisabled={costPackCanBeDisabled}
                    timeKeepingEnabled={rootEl.getAttribute('data-time-keeping-enabled') === '1'}
                    canManageTimeLock={rootEl.getAttribute('data-can-manage-time-lock') === '1'}
                    timeLockedUntil={rootEl.getAttribute('data-time-locked-until')}
                    timeLockUpdateUrl={rootEl.getAttribute('data-time-lock-update-url')}
                    canManageStatusRules={rootEl.getAttribute('data-can-manage-status-rules') === '1'}
                    statusRulesUrl={rootEl.getAttribute('data-status-rules-url')}
                />
            </ConfirmationModalProvider>
        </App>,
        rootEl,
    );
}
